import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubPageHeader } from "../../../components/ui/SubPageHeader";
import { FileText, Printer, Download } from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { ReportActionToolbar } from "../../../components/reports/ReportActionToolbar";
import { PaginationFooter } from "../../../components/sunbeds/PaginationFooter";
import {
  ZReportTable,
  ZReportItem,
} from "../../../components/reports/ZReportTable";
import { ReportDateRangePicker } from "../../../components/reports/ReportDateRangePicker";
import {
  startOfDay,
  endOfDay,
  subMonths,
  startOfMonth,
  subDays,
} from "date-fns";
import { useAuth } from "../../../auth/AuthContext";

const generateMockData = (currencySymbol: string): ZReportItem[] => {
  const today = new Date();
  return Array.from({ length: 25 }).map((_, i) => {
    const dateObj = subDays(today, i % 5);
    const spreadingDate = subDays(today, i * 2);

    return {
      id: `z-${i}`,
      date: spreadingDate.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
      dateObj: spreadingDate,
      noOfSales: 4 + (i % 3),
      totalNetSale: `${currencySymbol}${(300 + i * 10).toFixed(2)}`,
      totalProductSaleNet: `${currencySymbol}0.00`,
      totalServicesSaleNet: `${currencySymbol}${(300 + i * 10 + 4.5).toFixed(2)}`,
      totalTendering: `${currencySymbol}0.00`,
    };
  });
};

const ZReport: React.FC = () => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const navigate = useNavigate();
  const [reports] = useState<ZReportItem[]>(() => generateMockData(currencySymbol));
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: startOfMonth(subMonths(new Date(), 1)),
    end: new Date(),
  });

  const filteredReports = reports.filter((r) => {
    const matchesSearch = r.date
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    let matchesDate = true;
    if (dateRange.start && dateRange.end && r.dateObj) {
      matchesDate =
        r.dateObj >= startOfDay(dateRange.start) &&
        r.dateObj <= endOfDay(dateRange.end);
    }
    return matchesSearch && matchesDate;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentReports = filteredReports.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleView = (report: ZReportItem) => {
    return;
    navigate(`/reports/z-report/${report.id}`);
  };

  const handleSync = (report: ZReportItem) => {
    console.log("Sync/Refresh report:", report);
  };

  return (
    <div>
      <div className="flex-1 flex flex-col space-y-6 overflow-hidden p-4">
        <SubPageHeader
          title="Z-Report"
          subtitle="View and generate daily Z-Reports"
          icon={FileText}
          actions={
            <div className="flex items-center gap-3">
              <ReportDateRangePicker
                startDate={dateRange.start}
                endDate={dateRange.end}
                onChange={(start, end) => setDateRange({ start, end })}
              />
              <Button
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white flex items-center gap-2 px-3 py-2 shadow-sm"
                title="Print Reports"
              >
                <Printer size={18} />
              </Button>
            </div>
          }
        />

        <ReportActionToolbar
          totalItems={reports.length}
          showingStart={filteredReports.length > 0 ? indexOfFirstItem + 1 : 0}
          showingEnd={Math.min(indexOfLastItem, filteredReports.length)}
          searchTerm={searchTerm}
          onSearchChange={(val) => {
            setSearchTerm(val);
            setCurrentPage(1);
          }}
          searchPlaceholder="Search reports..."
        >
          <Button
            variant="outline"
            className="h-[38px] w-[38px] p-0 flex items-center justify-center border-gray-200 text-gray-500 hover:text-[#885ABB] hover:border-[#885ABB] bg-white"
          >
            <Download size={18} />
          </Button>
        </ReportActionToolbar>

        <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
          <ZReportTable
            reports={currentReports}
            isLoading={isLoading}
            indexOfFirstItem={indexOfFirstItem}
            onView={handleView}
            onSync={handleSync}
          />
          <PaginationFooter
            totalItems={reports.length}
            filteredTotal={filteredReports.length}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};
export default ZReport;
